import { brandApi } from "../lib/axios";
import { toast } from "react-hot-toast";
import { AxiosError } from "axios";
import {
  Campaign,
  CreateCampaignDto,
  UpdateCampaignDto,
  CampaignResponse,
  PaginatedCampaignsResponse,
  CampaignOverviewResponse,
} from "../types/campaign";

export class CampaignService {
  private readonly baseUrl = "/product-campaign";

  /**
   * Get campaigns with pagination and filters
   */
  async getCampaigns(
    page: number = 1,
    limit: number = 20,
    status?: string,
    sortOrder: "ASC" | "DESC" = "DESC"
  ): Promise<PaginatedCampaignsResponse> {
    try {
      const response = await brandApi.get(this.baseUrl, {
        params: {
          page,
          limit,
          status,
          sortOrder,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      if ((error as AxiosError)?.response?.status === 409) {
        const authUrl = (error as any).response.data.authorizationUrl;
        if (authUrl) {
          toast.error((error as any).response.data.message);
          window.location.href = `/brand/reauthorize?authUrl=${encodeURIComponent(
            authUrl
          )}`;
          return {} as PaginatedCampaignsResponse;
        }
      }
      throw error;
    }
  }

  /**
   * Get campaign sales overview
   */
  async getCampaignOverview(): Promise<CampaignOverviewResponse> {
    try {
      const response = await brandApi.get("/campaign-sales/overview");
      return response.data;
    } catch (error) {
      console.error("Error fetching campaign overview:", error);
      throw error;
    }
  }

  /**
   * Create a new campaign
   */
  async createCampaign(
    campaignData: CreateCampaignDto
  ): Promise<CampaignResponse> {
    try {
      const response = await brandApi.post(this.baseUrl, campaignData);
      return response.data;
    } catch (error) {
      console.error("Error creating campaign:", error);
      throw error;
    }
  }

  /**
   * Update an existing campaign
   */
  async updateCampaign(
    id: string,
    campaignData: UpdateCampaignDto
  ): Promise<CampaignResponse> {
    try {
      const response = await brandApi.patch(
        `${this.baseUrl}/${id}`,
        campaignData
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating campaign ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get a specific campaign by ID
   */
  async getCampaignById(id: string): Promise<Campaign> {
    try {
      const response = await brandApi.get(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch campaign ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a campaign
   */
  async deleteCampaign(id: string): Promise<void> {
    try {
      await brandApi.delete(`${this.baseUrl}/${id}`);
    } catch (error) {
      console.error(`Failed to delete campaign ${id}:`, error);
      throw error;
    }
  }

  /**
   * Toggle campaign active status
   */
  async toggleCampaignStatus(id: string, active: boolean): Promise<Campaign> {
    try {
      const endpoint = active ? "activate" : "deactivate";
      const response = await brandApi.post(`${this.baseUrl}/${id}/${endpoint}`);
      return response.data;
    } catch (error) {
      console.error(
        `Failed to ${active ? "activate" : "deactivate"} campaign ${id}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Activate a campaign
   */
  async activateCampaign(id: string): Promise<Campaign> {
    try {
      const response = await brandApi.post(`${this.baseUrl}/${id}/activate`);
      return response.data;
    } catch (error) {
      console.error(`Failed to activate campaign ${id}:`, error);
      throw error;
    }
  }

  /**
   * Deactivate a campaign
   */
  async deactivateCampaign(id: string): Promise<Campaign> {
    try {
      const response = await brandApi.post(`${this.baseUrl}/${id}/deactivate`);
      return response.data;
    } catch (error) {
      console.error(`Failed to deactivate campaign ${id}:`, error);
      throw error;
    }
  }
}

// Export singleton instance
export const campaignService = new CampaignService();
